import type {
  LocalizedString,
  MandateStatus,
  MandateTemplate,
  PortfolioCollectionPlatform,
  ProductListItem,
} from "@repo/shared/types";
import { Pencil, Trash2, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertMessage } from "@/components/ui/alert-message";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TranslationButton } from "@/components/ui/translation-button";
import {
  useDeleteMandateTemplate,
  useToggleMandateTemplateActive,
  useUpdateMandateTemplate,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";

import { MandateTemplateDialog } from "./mandate-template-dialog";
import { T } from "./mandate-templates-table.i18n";

type MandateTemplatesTableProps = {
  portfolioCollectionId: string;
  templates: MandateTemplate[];
  status: MandateStatus | undefined;
  riskScores: number[];
  platforms: PortfolioCollectionPlatform[];
  products: ProductListItem[];
};

/**
 * QL-375 — the templates of a collection (screen 05): one row per template,
 * sorted by risk score then name, with the configuration-health banner the
 * collection detail computes over ACTIVE templates.
 */
export function MandateTemplatesTable({
  portfolioCollectionId,
  templates,
  status,
  riskScores,
  platforms,
  products,
}: MandateTemplatesTableProps) {
  const { tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const getLabel = (i18n: LocalizedString) => localize(i18n);

  const toggleActive = useToggleMandateTemplateActive();
  const deleteTemplate = useDeleteMandateTemplate();
  const updateTemplate = useUpdateMandateTemplate();
  const isPending = toggleActive.isPending || deleteTemplate.isPending || updateTemplate.isPending;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MandateTemplate | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertDescription, setAlertDescription] = useState("");

  const showError = (description: string) => {
    setAlertDescription(description);
    setAlertOpen(true);
  };

  const platformLabel = (platformId: string) => {
    const platform = platforms.find((p) => p.id === platformId);
    return platform ? getLabel(platform.labelI18n) : platformId;
  };

  const templateName = (id: string) => {
    const template = templates.find((x) => x.id === id);
    return template ? getLabel(template.nameI18n) : id;
  };

  const warnings: string[] = [];
  if (status) {
    if (status.riskScoresWithoutTemplate.length > 0) {
      warnings.push(t.scoresWithoutTemplate(status.riskScoresWithoutTemplate.join(", ")));
    }
    if (status.untaggedTemplateIds.length > 0) {
      warnings.push(t.untagged(status.untaggedTemplateIds.map(templateName).join(", ")));
    }
    if (status.templatesWithInactiveProduct.length > 0) {
      warnings.push(
        t.inactiveProduct(status.templatesWithInactiveProduct.map(templateName).join(", ")),
      );
    }
  }

  const handleTranslationSave = (template: MandateTemplate, nameI18n: LocalizedString) => {
    updateTemplate.mutate(
      {
        collectionId: portfolioCollectionId,
        templateId: template.id,
        data: {
          nameI18n,
          riskScore: template.riskScore,
          mode: template.mode,
          platformIds: template.platformIds,
          lines: template.lines.map((l) =>
            template.mode === "fixed"
              ? { productId: l.productId, weightPct: l.weightPct }
              : {
                  productId: l.productId,
                  minPct: l.minPct,
                  defaultPct: l.defaultPct,
                  maxPct: l.maxPct,
                },
          ),
        },
      },
      { onError: (err: Error) => showError(err.message || t.failedToTranslate) },
    );
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h3 className="font-semibold">{t.title}</h3>
            <p className="text-sm text-muted-foreground">{t.description}</p>
          </div>
          <Button
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            disabled={isPending}
          >
            {t.addTemplate}
          </Button>
        </div>

        {warnings.length > 0 && (
          <Alert variant="warning">
            <TriangleAlert className="h-4 w-4" />
            <AlertDescription className="space-y-1">
              {warnings.map((w) => (
                <p key={w}>{w}</p>
              ))}
            </AlertDescription>
          </Alert>
        )}

        {templates.length === 0 ? (
          <EmptyState>{t.noTemplates}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">{t.riskScore}</TableHead>
                <TableHead>{t.template}</TableHead>
                <TableHead>{t.platforms}</TableHead>
                <TableHead>{t.mode}</TableHead>
                <TableHead className="text-right">{t.instruments}</TableHead>
                <TableHead>{t.active}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>
                    <span className="text-lg font-bold">{template.riskScore}</span>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">{getLabel(template.nameI18n)}</span>
                  </TableCell>
                  <TableCell>
                    {template.platformIds.length === 0 ? (
                      platforms.length > 0 ? (
                        <span className="flex items-center gap-1 text-xs text-warning">
                          <TriangleAlert className="h-3.5 w-3.5" />
                          {t.notTagged}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {template.platformIds.map((platformId) => (
                          <Badge key={platformId} variant="outline" className="text-xs">
                            {platformLabel(platformId)}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{template.mode === "fixed" ? t.modeFixed : t.modeMinMax}</TableCell>
                  <TableCell className="text-right">{template.lines.length}</TableCell>
                  <TableCell>
                    <Switch
                      aria-label={`${t.active}: ${getLabel(template.nameI18n)}`}
                      checked={template.isActive}
                      onCheckedChange={() =>
                        toggleActive.mutate(
                          { collectionId: portfolioCollectionId, templateId: template.id },
                          { onError: (err: Error) => showError(err.message || t.failedToToggle) },
                        )
                      }
                      disabled={isPending}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end">
                      <Button
                        size="icon"
                        tooltip={t.edit}
                        variant="ghost"
                        onClick={() => {
                          setEditing(template);
                          setDialogOpen(true);
                        }}
                        disabled={isPending}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <TranslationButton
                        value={template.nameI18n}
                        onSave={(nameI18n) => handleTranslationSave(template, nameI18n)}
                        supportedLanguages={tenantSupportedLanguages}
                        disabled={isPending}
                      />
                      <Button
                        size="icon"
                        tooltip={t.delete}
                        variant="ghost"
                        onClick={() => {
                          setToDelete(template.id);
                          setConfirmDeleteOpen(true);
                        }}
                        disabled={isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <MandateTemplateDialog
        portfolioCollectionId={portfolioCollectionId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        template={editing}
        riskScores={riskScores}
        platforms={platforms}
        products={products}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteTitle}
        description={t.deleteDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={() => {
          if (!toDelete) return;
          deleteTemplate.mutate(
            { collectionId: portfolioCollectionId, templateId: toDelete },
            {
              onSuccess: () => setToDelete(null),
              onError: (err: Error) => {
                showError(err.message || t.failedToDelete);
                setToDelete(null);
              },
            },
          );
        }}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={t.error}
        description={alertDescription}
        variant="destructive"
      />
    </>
  );
}
